import {
  ActivityStatusMiddleware,
  BFFWebChat,
  LoadingState,
  adaptiveCardARIARoleMessageMiddleware,
  androidAutoScrollBugMiddleware,
  bffAnswerAttachmentMiddleware,
  bffNubInjectorActivityMiddleware,
  buildMarkdownRenderer,
  getQueryParam,
  markdownLinkTargetMiddleware,
  messageBackTranscriptMiddleware,
  numericPIIMaskingMiddleware,
  openLinkedIntentUrlMiddleware,
  openUrlEventHandlerMiddleware,
  preventAcknowledgementOfInitialActivitiesMiddleware,
  removeKeyboardInstructionStrings,
  sessionMiddlewareFactory,
  skipReactionActivitiesMiddleware,
  useCustomSendbox,
  webChatReadyStoreMiddleware
} from '@sa/bff-webchat-ui';
import type { CassiusDirectLineConversationContext, CassiusWebChatConfig } from '@sa/cassius-contract';
import { createDirectLine, createStore, createStyleSet } from 'botframework-webchat';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { adaptiveCardsHostConfig, webChatStyleOptions } from './customStyles';
import { handleAppMessageEvent } from './messageEvents/appEventHandler';
import {
  channelSwapHandlerMiddleware,
  incomingAppMessageHandlerMiddleware,
  outgoingAppMessageHandlerMiddleware
} from './storeMiddleware';

const fetchConfig = async () => {
  const config: CassiusWebChatConfig = await fetch('/api/otp/conversationConfig', {
    method: 'GET',
    headers: {
      Authorization: `Bearer ${getQueryParam('Token') || ''}`,
      'Content-Type': 'application/json'
    }
  }).then((res) => res.json());
  return config;
};

const postConversationStartedEvent = (eventData: CassiusDirectLineConversationContext) => {
  // fire and forget
  fetch('/api/events/conversationStarted', {
    method: 'POST',
    body: JSON.stringify(eventData),
    headers: { 'Content-Type': 'application/json' }
  }).catch(/* ignore */);
};

const CassiusWebChat = () => {
  const [directLine, setDirectLine] = useState<ReturnType<typeof createDirectLine>>();
  const [store, setStore] = useState<ReturnType<typeof createStore>>();
  const [webChatReady, setWebchatReady] = useState(false);
  const [webChatConfig, setWebChatConfig] = useState<CassiusWebChatConfig>();

  const renderMarkdown = useMemo(
    () =>
      buildMarkdownRenderer({
        respectCRLF: !!webChatStyleOptions.markdownRespectCRLF,
        linkTargetPlugin: { enabled: false }
      }),
    [webChatStyleOptions]
  );

  const webchatContainerRef = useRef<HTMLDivElement>(null);
  useCustomSendbox(webchatContainerRef);

  const styleSet = useMemo(
    () => ({
      ...createStyleSet(webChatStyleOptions),
      // Workaround for bug in webchat not hiding upload button
      // https://stackoverflow.com/questions/65083636/microsoft-botframework-styleset-with-hideuploadbutton
      uploadButton: {
        display: 'none !important'
      }
    }),
    []
  );

  const webchatReadyMiddleware = webChatReadyStoreMiddleware(() => setWebchatReady(true));

  // Load the config from the server and initialise the webchat store and directline
  const initConversation = useCallback(async () => {
    setWebchatReady(false);
    const config = await fetchConfig();
    const middleware = [
      preventAcknowledgementOfInitialActivitiesMiddleware,
      numericPIIMaskingMiddleware,
      messageBackTranscriptMiddleware,
      webchatReadyMiddleware,
      outgoingAppMessageHandlerMiddleware,
      incomingAppMessageHandlerMiddleware,
      channelSwapHandlerMiddleware(config.channelSwapTimeout)
    ];

    if (config.session) {
      const sessionMiddleware = sessionMiddlewareFactory(config.session);
      if (sessionMiddleware) {
        sessionMiddleware.onExpired(() => {
          sessionMiddleware.destroy();
          initConversation();
        });

        middleware.push(sessionMiddleware.middleware);
      }
    }

    setStore(createStore({}, ...middleware));
    const directLineOptions: Parameters<typeof createDirectLine>[0] = {
      ...config.directLineConfig,
      webSocket: config.directLineConfig.webSocket ? undefined : (false as any)
    };
    setDirectLine(createDirectLine(directLineOptions));
    setWebChatConfig(config);
  }, [setStore, setDirectLine, setWebchatReady]);

  // Begin loading the conversation
  useEffect(() => {
    (async () => {
      await initConversation();
    })();
  }, []);

  useEffect(() => {
    if (webChatReady && webChatConfig) {
      postConversationStartedEvent({
        token: webChatConfig.directLineConfig.token,
        userId: webChatConfig.directLineConfig.userId,
        conversationId: webChatConfig.directLineConfig.conversationIdForToken,
        isResumed: webChatConfig.directLineConfig.continueConversation,
        userContext: webChatConfig.userContext,
        appCapabilities: webChatConfig.appCapabilities
      });
    }
  }, [webChatReady, webChatConfig]);

  useEffect(() => {
    if (store) {
      const handler = (event: MessageEvent) => {
        handleAppMessageEvent(event, store);
      };
      window.addEventListener('message', handler);
      return () => window.removeEventListener('message', handler);
    }
  }, [store]);

  return (
    <div className={'h-100'}>
      {webChatReady || <LoadingState />}
      <div className={'h-100' + (webChatReady ? '' : ' d-none')}>
        {directLine && (
          <div className={'h-100'} ref={webchatContainerRef}>
            <BFFWebChat
              activityMiddleware={[
                skipReactionActivitiesMiddleware,
                adaptiveCardARIARoleMessageMiddleware,
                androidAutoScrollBugMiddleware,
                bffNubInjectorActivityMiddleware,
                openLinkedIntentUrlMiddleware,
                openUrlEventHandlerMiddleware,
                markdownLinkTargetMiddleware()
              ]}
              overrideLocalizedStrings={() => removeKeyboardInstructionStrings}
              activityStatusMiddleware={ActivityStatusMiddleware(webChatStyleOptions)}
              adaptiveCardsHostConfig={adaptiveCardsHostConfig}
              attachmentMiddleware={bffAnswerAttachmentMiddleware}
              directLine={directLine}
              store={store}
              styleOptions={webChatStyleOptions}
              extraStyleSet={styleSet}
              renderMarkdown={renderMarkdown}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default CassiusWebChat;
